import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import CaisseController from './controllers/caisse-controller';
import { usePermissionViewModel } from './permissions/viewmodel/permission-viewmodel';
import CaisseClosureModal from './orders-components/CaisseClosureModal';
import CaisseCommandesModal from './orders-components/CaisseCommandesModal';

const formatTND = (n) => `${Number(n || 0).toFixed(3)} TND`;
const fmtDate = (d) => (d ? new Date(d).toLocaleString('fr-FR') : '—');

// Build the recette summary for a given caisse (loads all commandes, applies
// the same per-caisse received-amount logic as the closure modal).
const buildRecette = async (caisse) => {
  let allOrders = [];
  let p = 1;
  let maxPage = 1;
  do {
    const result = await CaisseController.paginateCommandes(caisse.id, p, 200);
    if (result && result.list) {
      allOrders = [...allOrders, ...result.list];
      maxPage = result.maxPage || 1;
    } else break;
    p++;
  } while (p <= maxPage);

  const validOrders = allOrders.filter((o) => o.etat !== 'canceled');
  const enriched = validOrders.map((o) => {
    let received = 0;
    if (o.caisseId === caisse.id) {
      if (o.caissePaiementId == null) received += o.montantPaye || 0;
      else received += o.montantInitialPaye || 0;
    }
    if (o.caissePaiementId === caisse.id && o.caisseId !== caisse.id) {
      received += (o.montantPaye || 0) - (o.montantInitialPaye || 0);
    }
    return { ...o, receivedHere: received };
  });

  // Fetch lines (product references) for each commande in parallel so the
  // recette ticket can list which products were sold per order.
  const withLines = await Promise.all(enriched.map(async (o) => {
    try {
      const full = await CaisseController.getCommande(o.id);
      const lines = full?.line || [];
      const refs = lines.map((l) => {
        const ref = l.product?.reference || l.productVariant?.reference || l.product?.name || '-';
        const variant = l.productVariant?.name ? ` (${l.productVariant.name})` : '';
        const qty = l.quantite || 1;
        const base = `${ref}${variant}`;
        return qty > 1 ? `${base} x${qty}` : base;
      });
      return { ...o, productRefs: refs };
    } catch {
      return { ...o, productRefs: [] };
    }
  }));
  const totalTTC = enriched.reduce((s, o) => s + o.receivedHere, 0);
  const fondCaisse = caisse.fondCaisse || 0;
  const totalCaisse = totalTTC + fondCaisse;

  const paiementMap = {};
  enriched.forEach((o) => {
    if (o.receivedHere <= 0) return;
    const mode = o.modePaiement || 'Espèce';
    if (!paiementMap[mode]) paiementMap[mode] = { count: 0, total: 0 };
    paiementMap[mode].count++;
    paiementMap[mode].total += o.receivedHere;
  });
  const byPaiement = Object.entries(paiementMap)
    .map(([mode, data]) => ({ mode, ...data }))
    .sort((a, b) => b.total - a.total);

  const preorders = enriched.filter((o) => (o.etat === 'pending') || ((o.totalTTC || 0) > (o.montantPaye || 0) + 0.0001));
  const preorderTotal = preorders.reduce((s, o) => s + (o.totalTTC || 0), 0);
  const preorderAvance = preorders.reduce((s, o) => s + (o.receivedHere || 0), 0);
  const preorderReste = preorders.reduce((s, o) => s + Math.max(0, (o.totalTTC || 0) - (o.montantPaye || 0)), 0);

  return {
    caisse,
    orders: withLines,
    totalTTC,
    fondCaisse,
    totalCaisse,
    byPaiement,
    nbCommandes: withLines.length,
    nbCanceled: allOrders.filter((o) => o.etat === 'canceled').length,
    preorders: { count: preorders.length, total: preorderTotal, avance: preorderAvance, reste: preorderReste },
  };
};

const printRecette = (recette) => {
  const { caisse, totalTTC, fondCaisse, totalCaisse, byPaiement, orders, nbCommandes, nbCanceled, preorders } = recette;
  const user = caisse.utilisateur
    ? `${caisse.utilisateur.firstName || ''} ${caisse.utilisateur.lastName || ''}`.trim() || caisse.utilisateur.userName
    : '—';
  const depot = caisse.depot?.name || '—';
  const ouv = caisse.dateDebut ? new Date(caisse.dateDebut) : null;
  const clo = caisse.dateFin ? new Date(caisse.dateFin) : null;
  const esc = (s) => (s == null ? '' : String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'));
  const ordersRows = orders.map((o, i) => {
    const isPre = (o.etat === 'pending') || ((o.totalTTC || 0) > (o.montantPaye || 0) + 0.0001);
    const refs = (o.productRefs || []).join(', ');
    const client = `${o.clientFirstName || ''} ${o.clientLastName || ''}`.trim();
    return `
    <tr>
      <td>${i + 1}</td>
      <td>${esc(o.reference || '-')}${isPre ? ' <b>[PRECO]</b>' : ''}</td>
      <td class="r">${(o.totalTTC || 0).toFixed(3)}</td>
      <td class="r">${(o.receivedHere || 0).toFixed(3)}</td>
    </tr>
    ${(client || refs) ? `<tr><td></td><td colspan="3" style="font-size:11px;padding-top:0;border-bottom:1px dotted #999">${esc(client)}${client && refs ? ' — ' : ''}${esc(refs)}</td></tr>` : ''}`;
  }).join('');
  const paiementRows = byPaiement.map((p) => `
    <div class="line"><span>${esc(p.mode)} (${p.count})</span><span>${p.total.toFixed(3)} TND</span></div>`).join('');
  const preorderBlock = preorders && preorders.count > 0 ? `
    <div class="sep"></div>
    <div class="center" style="font-weight:900;font-size:16px">PRECOMMANDES</div>
    <div class="line"><span>Nombre:</span><span>${preorders.count}</span></div>
    <div class="line"><span>Montant total:</span><span>${preorders.total.toFixed(3)} TND</span></div>
    <div class="line"><span>Avance encaissee:</span><span>${preorders.avance.toFixed(3)} TND</span></div>
    <div class="line"><span>Reste a encaisser:</span><span>${preorders.reste.toFixed(3)} TND</span></div>` : '';
  const printHtml = `<!doctype html><html><head><meta charset="utf-8"/><title>Recette caisse #${caisse.id}</title>
    <style>
      @page { size: 80mm auto; margin: 3mm; }
      * { margin: 0; padding: 0; box-sizing: border-box; color:#000 !important; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
      body { font-family: 'Arial Black','Helvetica',sans-serif; font-size: 15px; font-weight:700; width: 74mm; padding: 2mm; color:#000; line-height:1.35; }
      h1 { font-size: 26px; text-align: center; font-weight:900; letter-spacing:1px; }
      h2 { font-size: 17px; font-weight: 900; text-align: center; margin-top: 3px; }
      .sep { border-top: 2px dashed #000; margin: 8px 0; }
      .sep-bold { border-top: 3px solid #000; margin: 8px 0; }
      .line { display: flex; justify-content: space-between; padding: 3px 0; font-size: 15px; font-weight:700; }
      .line-big { display: flex; justify-content: space-between; padding: 5px 0; font-size: 20px; font-weight: 900; }
      .center { text-align: center; }
      table { width: 100%; border-collapse: collapse; margin: 4px 0; font-size: 13px; font-weight:700; }
      th, td { padding: 4px 3px; border-bottom: 1px solid #000; text-align: left; font-size:13px; }
      th { border-bottom: 2px solid #000; font-weight: 900; font-size:13px; }
      .r { text-align: right; }
    </style></head><body>
    <h1>TENDRESSE</h1>
    <h2>RECETTE CAISSE #${caisse.id}</h2>
    <div class="sep"></div>
    <div class="line"><span>Utilisateur:</span><span>${esc(user)}</span></div>
    <div class="line"><span>Depot:</span><span>${esc(depot)}</span></div>
    ${ouv ? `<div class="line"><span>Ouverture:</span><span>${ouv.toLocaleDateString('fr-FR')} ${ouv.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span></div>` : ''}
    ${clo ? `<div class="line"><span>Cloture:</span><span>${clo.toLocaleDateString('fr-FR')} ${clo.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span></div>` : `<div class="line"><span>Statut:</span><span>Ouverte</span></div>`}
    <div class="sep-bold"></div>
    <div class="line"><span>Commandes:</span><span>${nbCommandes}</span></div>
    ${nbCanceled > 0 ? `<div class="line"><span>Annulees:</span><span>${nbCanceled}</span></div>` : ''}
    <div class="line"><span>Encaisse:</span><span>${totalTTC.toFixed(3)} TND</span></div>
    <div class="line"><span>Fond de caisse:</span><span>${fondCaisse.toFixed(3)} TND</span></div>
    <div class="sep-bold"></div>
    <div class="line-big"><span>TOTAL:</span><span>${totalCaisse.toFixed(3)} TND</span></div>
    ${byPaiement.length ? `<div class="sep"></div><div class="center" style="font-weight:900;font-size:16px">PAR MODE DE PAIEMENT</div>${paiementRows}` : ''}
    ${preorderBlock}
    ${orders.length ? `<div class="sep"></div><div class="center" style="font-weight:900;font-size:16px">DETAIL DES COMMANDES</div>
      <table><thead><tr><th>#</th><th>Ref</th><th class="r">Total</th><th class="r">Recu</th></tr></thead><tbody>${ordersRows}</tbody></table>` : ''}
    <div class="sep"></div>
    <div class="center" style="font-size:14px;font-weight:900">Merci - Tendresse</div>
  </body></html>`;

  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.setAttribute('aria-hidden', 'true');
  document.body.appendChild(iframe);
  const cleanup = () => { try { document.body.removeChild(iframe); } catch (e) {} };
  iframe.onload = () => {
    try {
      const w = iframe.contentWindow;
      setTimeout(() => {
        try { w.focus(); w.print(); } catch (e) { console.error(e); }
        w.onafterprint = () => setTimeout(cleanup, 200);
        setTimeout(cleanup, 60000);
      }, 150);
    } catch (e) { console.error(e); cleanup(); }
  };
  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open();
  doc.write(printHtml);
  doc.close();
};

const CaissesListPage = () => {
  const { hasPermission } = usePermissionViewModel('liste_caisses');
  const canPrint = hasPermission('print') || hasPermission('write');
  const [loading, setLoading] = useState(true);
  const [caisses, setCaisses] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [filterCloture, setFilterCloture] = useState(''); // '' | 'true' | 'false'
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [printingId, setPrintingId] = useState(null);
  const [closingCaisseId, setClosingCaisseId] = useState(null);
  const [commandesCaisse, setCommandesCaisse] = useState(null);
  const canWrite = hasPermission('write');

  const handlePrintRecette = async (caisse) => {
    setPrintingId(caisse.id);
    try {
      const recette = await buildRecette(caisse);
      printRecette(recette);
    } catch (e) {
      console.error(e);
      toast.error('Impossible de générer la recette');
    } finally {
      setPrintingId(null);
    }
  };

  const fetchCaisses = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCloture !== '') params.Cloture = filterCloture;
      if (fromDate) params.From = fromDate;
      if (toDate) params.To = toDate;
      const r = await CaisseController.paginate(page, pageSize, params);
      setCaisses(r?.list || []);
      setTotalItems(r?.total || 0);
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors du chargement des caisses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCaisses(); /* eslint-disable-next-line */ }, [page, filterCloture, fromDate, toDate]);

  const maxPage = useMemo(() => Math.max(1, Math.ceil(totalItems / pageSize)), [totalItems]);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Liste des caisses</h1>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Statut</label>
          <select
            value={filterCloture}
            onChange={(e) => { setPage(1); setFilterCloture(e.target.value); }}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
          >
            <option value="">Toutes</option>
            <option value="false">Ouvertes</option>
            <option value="true">Clôturées</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Du</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => { setPage(1); setFromDate(e.target.value); }}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Au</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => { setPage(1); setToDate(e.target.value); }}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
          />
        </div>
        <button
          onClick={() => { setFilterCloture(''); setFromDate(''); setToDate(''); setPage(1); }}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Réinitialiser
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">#</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Utilisateur</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Dépôt</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Ouverture</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Clôture</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Fond</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Encaissé</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Statut</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-500">Chargement…</td></tr>
              ) : caisses.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-500">Aucune caisse trouvée.</td></tr>
              ) : caisses.map((c) => {
                const userName = c.utilisateur
                  ? `${c.utilisateur.firstName || ''} ${c.utilisateur.lastName || ''}`.trim() || c.utilisateur.userName
                  : '—';
                return (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">#{c.id}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{userName}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{c.depot?.name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{fmtDate(c.dateDebut)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{fmtDate(c.dateFin)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700">{formatTND(c.fondCaisse)}</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">{formatTND(c.montantTotal)}</td>
                    <td className="px-4 py-3 text-center">
                      {c.cloture ? (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">Clôturée</span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-700">Ouverte</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setCommandesCaisse(c)}
                          className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-md"
                          title="Voir les commandes de cette caisse"
                        >
                          <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                          </svg>
                          Commandes
                        </button>
                        {!c.cloture && canWrite && (
                          <button
                            type="button"
                            onClick={() => setClosingCaisseId(c.id)}
                            className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-md"
                            title="Clôturer cette caisse"
                          >
                            <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                            Clôturer
                          </button>
                        )}
                        {canPrint ? (
                          <button
                            type="button"
                            onClick={() => handlePrintRecette(c)}
                            disabled={printingId === c.id}
                            className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-md disabled:opacity-50"
                            title="Imprimer la recette de la caisse"
                          >
                            <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                            </svg>
                            {printingId === c.id ? '...' : 'Recette'}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {totalItems > pageSize && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <button
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-4 py-2 text-sm rounded-md border border-gray-300 bg-white disabled:opacity-50"
            >
              ← Précédent
            </button>
            <span className="text-sm text-gray-600">Page {page} / {maxPage} • {totalItems} résultat(s)</span>
            <button
              disabled={page >= maxPage || loading}
              onClick={() => setPage((p) => Math.min(maxPage, p + 1))}
              className="px-4 py-2 text-sm rounded-md border border-gray-300 bg-white disabled:opacity-50"
            >
              Suivant →
            </button>
          </div>
        )}
      </div>

      <CaisseClosureModal
        isOpen={!!closingCaisseId}
        caisseId={closingCaisseId}
        onClose={() => { setClosingCaisseId(null); fetchCaisses(); }}
      />

      <CaisseCommandesModal
        isOpen={!!commandesCaisse}
        caisse={commandesCaisse}
        canDelete={canWrite}
        onChanged={fetchCaisses}
        onClose={() => setCommandesCaisse(null)}
      />

    </div>
  );
};

export default CaissesListPage;
